import { useState, useContext, useEffect } from 'react';
import { KeyboardArrowUp, MoreVert, KeyboardArrowLeft, Add } from '@mui/icons-material';
import {
    Alert,
    AlertTitle,
    Avatar,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Snackbar,
    TableCell,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { decode } from 'html-entities';
// Our Components
import { AuthContext } from 'context/auth-context';
import { CardContext } from 'context/cards/cardContext';
import {
    HIDE_CARD_INFO,
    OPEN_EDIT_CARD_FUNDS_MODAL,
    SET_CARD,
    OPEN_REVIEW_NEW_CARD,
    GET_CARDS,
    OPEN_REQUEST_EDIT_CARD_LIMIT
} from 'context/cards/types';
import AlertDialog from 'components/shared/AlertDialog/AlertDialog';
import BlueToolTip from 'components/shared/BlueTooltip';
import CardMenuItem from './CustomMenuItem';
import ConfirmationModal from 'components/shared/ConfirmationModal';
import CVVSwitch from 'components/shared/CVVSwitch';
import DeliveryStatusTooltipTitle from './DeliveryStatusTooltipTitle';
import PaystandCard from './PaystandCard/PaystandCard';
import PhysicalCardStatus from './PhysicalCardStatus';
import useCardSettings from 'Hooks/useCardSettings';
import VirtualCardStatus from './VirtualCardStatus';
// Services
import { Cards } from 'lib/api';
// Utils and Helpers
import { CARD_TYPE, SOLIDFI_CARD_STATUS, DEFI_CARD_STATUS } from 'helpers/constants';
import { hasCreditModel, hasDepositModel, hasPINModel, hasPrepaidModel } from 'helpers/businesses';
import { isAdminUser } from 'helpers/auth';
import { numberFormat } from 'helpers/i18n';
// Assets
import { ReactComponent as RequestIcon } from 'assets/cardsSection/requestIcon.svg';
import './CardTableRow.css';

const TIME_TO_HIDE_CARD_INFO = 1000 * 60; // 1 minute
const { REACT_APP_AR_FRONTEND_URL } = process.env;

const CardTableRow = ({ row, requestedCardByEmail }) => {
    const { currentCard, shouldUpdateCardList, dispatch, statistics } = useContext(CardContext);
    const { businessInfo, userInfo } = useContext(AuthContext);
    const [isCardDetailsOpen, setIsCardDetailsOpen] = useState(false);
    const [isSpendDialogOpen, setIsSpendDialogOpen] = useState(false);
    const [isButtonSpendClicked, setIsButtonSpendClicked] = useState(false);
    const [isActionDenied, setIsActionDenied] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [showCardInfo, setShowCardInfo] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
    const isCardMenuOpen = Boolean(anchorEl);
    const [amount, setAmount] = useState(0);
    const { solidData: solidfiCard } = row;
    const cardSettings = useCardSettings();

    useEffect(() => {
        if (shouldUpdateCardList) {
            handleCloseCardMenu();
        }
    }, [shouldUpdateCardList]);

    useEffect(() => {
        if (!currentCard) return;

        const hideCardInfoTimer = setTimeout(() => {
            dispatch({ type: HIDE_CARD_INFO });
        }, TIME_TO_HIDE_CARD_INFO);
        return () => clearTimeout(hideCardInfoTimer);
    }, [currentCard, dispatch]);

    useEffect(() => {
        if (requestedCardByEmail?.id) {
            dispatch({ type: SET_CARD, payload: requestedCardByEmail });
            dispatch({ type: OPEN_REVIEW_NEW_CARD });
        }
    }, [dispatch, requestedCardByEmail]);

    const toggleCardDetails = () => {
        if (!solidfiCard) return;

        setIsCardDetailsOpen(isMemoInfoOpen => !isMemoInfoOpen);
        if (!isCardDetailsOpen) {
            dispatch({ type: HIDE_CARD_INFO });
            setShowCardInfo(false);
        }
    };

    const handleCardMenuClick = (event, cardStatus) => {
        if (businessInfo?.is_frozen) return;
        if (cardStatus) return setIsActionDenied(true);
        setAnchorEl(event.currentTarget);
    };

    const handleCloseCardMenu = () => {
        setAnchorEl(null);
    };

    const handleCardSpendClick = async () => {
        try {
            setIsButtonSpendClicked(true);
            const body = {
                id: row.id,
                amount: amount
            };
            await Cards.testCard(body);
            setIsButtonSpendClicked(false);
            setIsSpendDialogOpen(false);
        } catch (error) {
            console.error(error);
        }
    };

    const getAvailableLimit = () => {
        const availableLimit = Number(row.balance);
        const fboInternalBalance = Number(businessInfo?.account?.internal_balance);

        if (hasCreditModel(businessInfo) && fboInternalBalance < availableLimit) {
            return numberFormat(businessInfo.account.internal_balance, 2, 2);
        }
        return numberFormat(row.balance, 2, 2);
    };

    const handleAddFundsButtonClick = async () => {
        if (hasPrepaidModel(businessInfo) && statistics.availableToAddtoCards <= 0) {
            return setIsAlertDialogOpen(true);
        }
        try {
            const cardData = await Cards.getCard(row.id);
            dispatch({
                type: SET_CARD,
                payload: cardData
            });
            dispatch({ type: OPEN_EDIT_CARD_FUNDS_MODAL });
        } catch (error) {
            console.error(error);
        }
    };

    const handleRejectCardRequestClick = async () => {
        try {
            await Cards.rejectCardRequest(row.id);
            handleCloseCardMenu();
            dispatch({ type: GET_CARDS });
        } catch (error) {
            console.error(error);
        }
    };

    const handleRequestEditLimitClick = async () => {
        try {
            const cardData = await Cards.getCard(row.id);
            dispatch({
                type: OPEN_REQUEST_EDIT_CARD_LIMIT,
                payload: cardData
            });
        } catch (error) {
            console.error(error);
        }
    };

    const isActiveCard = () => !row.isDeactivated && !row.isCanceled && !row.is_system_deactivated;

    return (
        <>
            <TableRow>
                <TableCell>
                    <Box display="flex" alignItems="center" gap="10px">
                        <IconButton disabled={Boolean(row.isCanceled)} size="small" onClick={toggleCardDetails}>
                            {isCardDetailsOpen ? <KeyboardArrowUp /> : <KeyboardArrowLeft />}
                        </IconButton>
                        <Box>
                            {row.type === CARD_TYPE.VIRTUAL ? (
                                <Box className="card-table-row__card-img-virtual"></Box>
                            ) : (
                                <Box className="card-table-row__card-img-physical"></Box>
                            )}
                        </Box>
                    </Box>
                </TableCell>
                <TableCell className="card-table-row__cell">
                    <Box
                        className="fw-bold"
                        sx={{ minWidth: 100, maxWidth: 150, marginBottom: '5px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.name}
                    </Box>
                    {row.status !== DEFI_CARD_STATUS.REQUESTED && (
                        <Box className="card-table-row__secondary-text">**** {row.last4}</Box>
                    )}
                </TableCell>
                <TableCell
                    className="card-table-row__cell"
                    sx={{ minWidth: 100, maxWidth: 150, marginBottom: '5px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {decode(row.label, { level: 'html5' })}
                </TableCell>
                <TableCell
                    className="card-table-row__cell"
                    sx={{ minWidth: 100, maxWidth: 150, marginBottom: '5px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.department ? row.department : row?.cardholder?.department}
                </TableCell>
                {hasCreditModel(businessInfo) && (
                    <TableCell className="card-table-row__cell">{row.has_manual_replenish ? 'Manual' : 'Automatic'}</TableCell>
                )}
                <TableCell>
                    {row?.type === CARD_TYPE.PHYSICAL && isActiveCard() && row.status !== DEFI_CARD_STATUS.REQUESTED ? (
                        <BlueToolTip
                            className="tracking-number-tooltip"
                            placement="top"
                            arrow
                            title={<DeliveryStatusTooltipTitle solidfiCard={solidfiCard} />}>
                            <PhysicalCardStatus card={row} />
                        </BlueToolTip>
                    ) : (
                        <VirtualCardStatus card={row} />
                    )}
                </TableCell>
                {hasCreditModel(businessInfo) && (
                    <TableCell className="card-table-row__cell" align="right">
                        <Box className="fw-bold">{numberFormat(row.limitAmount, 2, 2)}</Box>
                        {row?.requested_limit_amount && (
                            <>
                                <Box className="card-table-row__secondary-text">Requested</Box>
                                <Box className="card-table-row__secondary-text">
                                    {numberFormat(row.requested_limit_amount, 2, 2)}
                                </Box>
                            </>
                        )}
                    </TableCell>
                )}
                <TableCell
                    className={`card-table-row__cell ${
                        Number(row.balance) === 0 && row.isCanceled === 0 && 'card-table-row__card-no-balance'
                    }`}
                    align="right">
                    <span className="fw-bold">{getAvailableLimit()}</span>
                </TableCell>
                {/* Card Options */}
                <TableCell>
                    {isActiveCard() && hasPrepaidModel(businessInfo) && Number(row.balance) === 0 && (
                        <Button variant="outlined" className="card-table-row__add-funds-btn" onClick={handleAddFundsButtonClick}>
                            <Add fontSize="small" />
                        </Button>
                    )}
                    {cardSettings.isCreationEnabled ? (
                        <Tooltip title="Card Options">
                            <span>
                                <IconButton
                                    data-testid="card-actions-menu"
                                    disabled={Boolean(row.isCanceled)}
                                    size="small"
                                    sx={{ marginLeft: '5px' }}
                                    onClick={e => handleCardMenuClick(e, row.is_system_deactivated)}>
                                    <Avatar
                                        sx={{
                                            width: '32px',
                                            height: '32px',
                                            bgcolor: 'white',
                                            borderColor: '#A8B6C1',
                                            color: '#A8B6C1'
                                        }}>
                                        {row.status === DEFI_CARD_STATUS.REQUESTED && isAdminUser(userInfo?.role?.id) ? (
                                            <RequestIcon />
                                        ) : (
                                            <MoreVert />
                                        )}
                                    </Avatar>
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : (
                        <BlueToolTip
                            placement="left"
                            arrow
                            title="The card options is temporarily unavailable due to system maintenance.">
                            <span>
                                <IconButton
                                    data-testid="card-actions-menu"
                                    disabled
                                    size="small"
                                    sx={{ marginLeft: '5px' }}
                                    onClick={e => handleCardMenuClick(e, row.is_system_deactivated)}>
                                    <Avatar
                                        sx={{
                                            width: '32px',
                                            height: '32px',
                                            bgcolor: 'white',
                                            borderColor: '#A8B6C1',
                                            color: '#A8B6C1'
                                        }}>
                                        <MoreVert />
                                    </Avatar>
                                </IconButton>
                            </span>
                        </BlueToolTip>
                    )}
                </TableCell>
                {isAdminUser(userInfo?.role?.id) ? (
                    <>
                        {row.status !== DEFI_CARD_STATUS.REQUESTED ? (
                            <Menu anchorEl={anchorEl} open={isCardMenuOpen} onClose={handleCloseCardMenu}>
                                {!row.is_system_deactivated && (
                                    <div>
                                        {!row.isDeactivated && (
                                            <>
                                                {process.env.REACT_APP_API_URL !== process.env.REACT_APP_API_URL_PRODUCTION && (
                                                    <MenuItem
                                                        onClick={() => {
                                                            setIsSpendDialogOpen(true);
                                                            setAnchorEl(null);
                                                        }}>
                                                        Spend
                                                    </MenuItem>
                                                )}
                                                {solidfiCard.cardStatus !== SOLIDFI_CARD_STATUS.INACTIVE && (
                                                    <CardMenuItem
                                                        cardId={row.id}
                                                        edit
                                                        closeMenu={handleCloseCardMenu}
                                                        setIsAlertDialogOpen={setIsAlertDialogOpen}
                                                    />
                                                )}
                                                {solidfiCard.cardStatus !== SOLIDFI_CARD_STATUS.INACTIVE &&
                                                    (hasDepositModel(businessInfo) || hasPINModel(businessInfo)) && (
                                                        <CardMenuItem
                                                            cardId={row.id}
                                                            editFunds
                                                            closeMenu={handleCloseCardMenu}
                                                            setIsAlertDialogOpen={setIsAlertDialogOpen}
                                                        />
                                                    )}
                                            </>
                                        )}
                                        <CardMenuItem
                                            cardId={row.id}
                                            cardStatus={solidfiCard.cardStatus}
                                            isDeactivated={row.isDeactivated}
                                        />
                                    </div>
                                )}
                                {!row.isDeactivated && <CardMenuItem cardId={row.id} cancelCard />}
                            </Menu>
                        ) : (
                            <Menu anchorEl={anchorEl} open={isCardMenuOpen} onClose={handleCloseCardMenu}>
                                <>
                                    <MenuItem
                                        onClick={() => {
                                            dispatch({ type: OPEN_REVIEW_NEW_CARD, payload: row });
                                            handleCloseCardMenu();
                                        }}>
                                        Review
                                    </MenuItem>
                                    <MenuItem
                                        onClick={() => {
                                            setIsConfirmationModalOpen(true);
                                        }}>
                                        Reject
                                    </MenuItem>
                                    <ConfirmationModal
                                        open={isConfirmationModalOpen}
                                        close={() => {
                                            setIsConfirmationModalOpen(isConfirmationModalOpen => !isConfirmationModalOpen);
                                            handleCloseCardMenu();
                                        }}
                                        action={'reject'}
                                        nextFuntion={handleRejectCardRequestClick}
                                        component={'request'}
                                    />
                                </>
                            </Menu>
                        )}
                    </>
                ) : (
                    <>
                        {row.status !== DEFI_CARD_STATUS.REQUESTED && (
                            <Menu anchorEl={anchorEl} open={isCardMenuOpen} onClose={handleCloseCardMenu}>
                                <MenuItem onClick={handleRequestEditLimitClick}>Request Limit Change</MenuItem>
                            </Menu>
                        )}
                    </>
                )}
            </TableRow>
            {/* Card Memo */}
            {row?.memo && (
                <TableRow>
                    <TableCell className="card-table-row__cell" sx={{ padding: '0px' }}>
                        <Box className="card-table-row__memo-container"></Box>
                    </TableCell>
                    <TableCell className="card-table-row__cell" colSpan="9" sx={{ padding: '0px' }}>
                        <Box className="card-table-row__memo-container">
                            <Typography paragraph className="card-table-row__memo">
                                <span className="card-table-row__memo-label">memo:</span>
                                <span className="card-table-row__memo-message">{decode(row.memo, { level: 'html5' })}</span>
                            </Typography>
                        </Box>
                    </TableCell>
                </TableRow>
            )}
            {row.status !== DEFI_CARD_STATUS.REQUESTED && (
                <TableRow>
                    <TableCell style={{ padding: 0 }} colSpan={10}>
                        <Collapse in={isCardDetailsOpen} timeout="auto" unmountOnExit>
                            <Grid container className="bg-gray">
                                <Grid item xs={4} md={3}>
                                    <PaystandCard
                                        card={solidfiCard}
                                        showCardInfo={showCardInfo}
                                        setShowCardInfo={setShowCardInfo}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs={9}
                                    className="card-table-row__card-metadata"
                                    alignSelf="center"
                                    sx={{ paddingLeft: '50px' }}>
                                    <FormControlLabel
                                        control={<CVVSwitch card={row} checked={showCardInfo} setChecked={setShowCardInfo} />}
                                        label={showCardInfo ? 'Hide Card Details' : 'Show Card Details'}
                                        sx={{ marginBottom: '20px' }}
                                    />
                                    <Typography mt={4} className="card-table-row__label">
                                        Auto lock date
                                    </Typography>
                                    <Typography className="card-table-row__value">
                                        {row.autoLock ? new Date(row.autoLock).toLocaleDateString() : '-'}
                                    </Typography>

                                    <Typography mt={4} className="card-table-row__label">
                                        Categories Allowed
                                    </Typography>
                                    <Typography className="card-table-row__value">All allowed</Typography>

                                    <Typography mt={4} className="card-table-row__label">
                                        Billing Address
                                    </Typography>
                                    <Typography className="card-table-row__value">
                                        {solidfiCard?.cardholder?.billingAddress?.line1},{' '}
                                        {solidfiCard?.cardholder?.billingAddress?.city},{' '}
                                        {solidfiCard?.cardholder?.billingAddress?.state}{' '}
                                        {solidfiCard?.cardholder?.billingAddress?.country},{' '}
                                        {solidfiCard?.cardholder?.billingAddress?.postalCode}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}

            {/* ALERTS/FEEDBACK */}
            {hasDepositModel(businessInfo) && (
                <AlertDialog
                    isOpen={isAlertDialogOpen}
                    title={`You don't have funds available to add to cards.`}
                    description="Please add money to your account to be able to add card funds."
                    onButtonClick={() => setIsAlertDialogOpen(false)}
                />
            )}
            {hasPINModel(businessInfo) && (
                <AlertDialog
                    isOpen={isAlertDialogOpen}
                    title={`You don't have funds available to add to cards.`}
                    description="Please add money to your account to create a new card."
                    buttonText="Add Funds"
                    onButtonClick={() => {
                        window.open(
                            `${REACT_APP_AR_FRONTEND_URL}/v2/ar/customers/${businessInfo.business_ar_id}/overview`,
                            '_blank'
                        );
                        setIsAlertDialogOpen(false);
                    }}
                />
            )}
            <Snackbar
                open={isActionDenied}
                onClose={() => setIsActionDenied(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" variant="filled">
                    <AlertTitle>Action Denied</AlertTitle>
                    You can not modify Cards deactivated by System
                </Alert>
            </Snackbar>
            <Dialog open={isSpendDialogOpen} onClose={() => setIsSpendDialogOpen(false)} style={{ maxHeight: '20em' }}>
                <DialogTitle>Spend from card</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="name"
                        label="Send Amount"
                        type="email"
                        fullWidth
                        variant="standard"
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                    />
                    <Button variant="contained" onClick={handleCardSpendClick} disabled={isButtonSpendClicked}>
                        Accept
                    </Button>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default CardTableRow;
